import { Component, OnInit } from '@angular/core';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { Router } from '@angular/router';
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { take } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RoutesKeys } from 'src/app/utils/routes';
import { EphemeralFeedback } from 'src/app/shared/models/ephemeral-feedback/ephemeral-feedback';
import {
  BusinessWebsiteAlreadyExistsException,
  RepresentativeAlreadyExistsException,
} from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { environment } from '../../../environments/environment';

@Component({
  templateUrl: './register.page.html',
})
/**
 * Shall instantiate RegisterPage template
 */
export class RegisterPage implements OnInit {
  nextPageLabel: string = 'next-step';
  previousPageLabel: string = 'previous-step';
  backToLoginLabel: string = 'back-to-login';
  finishLabel: string = 'create-account';
  carouselPages: string[] = ['page-1', 'page-2', 'page-3'];
  firstPageLabel: string = 'first-register-page-header';
  secondPageLabel: string = 'second-register-page-header';
  thirdPageLabel: string = 'third-register-page-header';
  termsFormGroup!: UntypedFormGroup;
  recaptchaFormGroup!: FormGroup;
  businessData!: BusinessDataFormObject;
  equipmentData!: BusinessEquipmentFormObject;
  businessRepresentative!: BusinessRepresentativeFormObject;
  creatingAccount = false;
  errorMessage = new EphemeralFeedback();
  email = '';
  readonly labels = {
    registerErrorGeneric: 'register-error-generic',
  };
  recaptchaEnabled = environment.recaptcha.enabled;

  /**
   * @constructor
   */
  constructor(
    private registerService: RegisterService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.businessData = this.registerService.getBusinessDataFormObject();
    this.equipmentData = this.registerService.getBusinessEquipmentFormObject();
    this.businessRepresentative =
      this.registerService.businessRepresentativeFormObject;

    this.termsFormGroup = new UntypedFormGroup({
      agree: new UntypedFormControl(false, [Validators.requiredTrue]),
    });

    this.recaptchaFormGroup = new FormGroup({
      recaptcha: new FormControl(
        null,
        this.recaptchaEnabled ? Validators.required : []
      ),
    });

    this.registerService
      .getTermsOfUseAndPrivacyConditionsURLs()
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.businessRepresentative.termsAndConditionsLink =
            res.termsOfUseURL;
          this.businessRepresentative.privacyPolicyLink =
            res.privacyConditionsURL;
        },
        error: () => {
          this.termsFormGroup.disable();
        },
      });
  }

  /**
   * User register action
   */
  async register() {
    this.email = this.businessRepresentative.formGroup.controls['email'].value;
    this.authenticationService.passwordResetEmail = this.email;
    const { recaptcha } = this.recaptchaFormGroup.getRawValue();
    const recaptchaToken = this.recaptchaEnabled ? recaptcha : 'dev-bypass';
    this.creatingAccount = true;
    try {
      await this.registerService.registerBusiness(recaptchaToken);
      this.router.navigate([RoutesKeys.VERIFICATION], {
        queryParams: { e: this.email },
      });
    } catch (e) {
      this.dealWithRegisterError(e as Error);
    }
    this.creatingAccount = false;
  }

  /**
   * deals with register errors
   * @param {Error} e
   */
  private dealWithRegisterError(e: Error) {
    switch (e.constructor) {
      case BusinessWebsiteAlreadyExistsException: {
        const [, website] =
          e.message.match(/Business website (.*) already exists./) ?? [];
        this.translationService
          .translateString('business-website-already-exists-exception', {
            website,
          })
          .then((error) => {
            this.errorMessage.feedback = error;
          });
        return;
      }
      case RepresentativeAlreadyExistsException: {
        this.translationService
          .translateString('register-error-rae', { email: this.email })
          .then((error) => {
            this.errorMessage.feedback = error;
          });
        return;
      }
      default: {
        this.translationService
          .translateString(this.labels.registerErrorGeneric)
          .then((error) => {
            this.errorMessage.feedback = error;
          });
      }
    }
  }
}
